/**
 * Convert single items to a one item array and return arrays unchanged.
 * This is different from Array.from in that it will not blow up your strings and other iterables.
 * Null will be converted to an array with a single null in it.
 * Undefined will be converted to an empty array.
 *
 * @example
 * safeToArray('Hello')
 * // => ['Hello']
 *
 * @example
 * safeToArray(null)
 * // => [null]
 *
 * @example
 * safeToArray(undefined)
 * // => []
 *
 * @example
 * safeToArray([1, undefined, 2])
 * // => [1, undefined, 2]
 *
 * @example
 * safeToArray({greeting: 'Ciao'});
 * // => [{greeting: 'Ciao'}]
 */
export const safeToArray = <T>(value: T | Array<T> | undefined): Array<T> => {
  if (value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [value];
};

// Fixed-length tuple of a specific length N
export type FixedLengthArray<T, N extends number> = N extends N
  ? number extends N
    ? T[] // fallback if N is generic number
    : _FixedLengthArray<T, N, []>
  : never;

type _FixedLengthArray<T, N extends number, Acc extends readonly unknown[]> = Acc['length'] extends N
  ? Acc
  : _FixedLengthArray<T, N, [...Acc, T]>;
